import { useState, useRef, useEffect, useMemo } from "react";
import { SearchSelect, SearchSelectItem, TextInput, Icon } from "@tremor/react";
import { XCircleIcon } from "@heroicons/react/solid";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import { useNavigate, useLocation } from "react-router-dom";
import { greekCourts } from "../courts";
import { truncateCourt } from "../utils/truncateCourt";

const AddCase = ({
  handleClickAddCaseModal,
  allCases,
  handleAddCase,
  credits,
  setCredits,
}) => {
  const [gak, setGak] = useState("");

  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear.toString());
  const [court, setCourt] = useState("");
  const [hasUserSelectedCourt, setHasUserSelectedCourt] = useState(false);
  const [note, setNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Validation states
  const [gakError, setGakError] = useState("");
  const [yearError, setYearError] = useState("");
  const [courtError, setCourtError] = useState("");

  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const location = useLocation();
  const errRef = useRef();

  const [errMsg, setErrMsg] = useState("");

  const recentCourts = useMemo(() => {
    const seenCourts = new Set();
    const recent = [];

    allCases.forEach((singleCase) => {
      const courtValue = singleCase?.court?.trim();

      if (!courtValue || seenCourts.has(courtValue)) {
        return;
      }

      seenCourts.add(courtValue);
      recent.push(courtValue);
    });

    return recent.slice(0, 5);
  }, [allCases]);

  const options = useMemo(() => {
    const seenCourts = new Set();

    return [...recentCourts, ...greekCourts]
      .map((courtValue) => courtValue?.trim())
      .filter((courtValue) => {
        if (!courtValue || seenCourts.has(courtValue)) {
          return false;
        }

        seenCourts.add(courtValue);
        return true;
      })
      .map((courtValue) => ({
        label: truncateCourt(courtValue),
        value: courtValue,
      }));
  }, [recentCourts]);

  // const gakRef = useRef();

  // useEffect(() => {
  //   gakRef.current.focus();
  // }, [])

  useEffect(() => {
    if (!hasUserSelectedCourt && !court && recentCourts[0]) {
      setCourt(recentCourts[0]);
      setCourtError("");
    }
  }, [court, hasUserSelectedCourt, recentCourts]);

  const handleCourtChange = (value) => {
    setHasUserSelectedCourt(true);
    setCourt(value ?? "");
    // Validate court selection
    if (!value || value === "") {
      setCourtError("Παρακαλώ επιλέξτε δικαστήριο");
    } else {
      setCourtError("");
    }
  };

  const handleGakChange = (value) => {
    setGak(value);
    // Validate GAK
    const gakNum = parseInt(value);
    if (!value || isNaN(gakNum) || gakNum < 1 || gakNum > 1000000) {
      setGakError("ΓΑΚ πρέπει να είναι μεταξύ 1 και 1000000");
    } else {
      setGakError("");
    }
  };

  const handleYearChange = (value) => {
    setYear(value);
    // Validate year
    const yearNum = parseInt(value);
    if (!value || isNaN(yearNum) || yearNum < 2010 || yearNum > currentYear) {
      setYearError(`Έτος πρέπει να είναι μεταξύ 2010 και ${currentYear}`);
    } else {
      setYearError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields before submission
    const gakNum = parseInt(gak);
    const yearNum = parseInt(year);

    let hasErrors = false;

    if (!gak || isNaN(gakNum) || gakNum < 1 || gakNum > 1000000) {
      setGakError("ΓΑΚ πρέπει να είναι μεταξύ 1 και 1000000");
      hasErrors = true;
    }

    if (!year || isNaN(yearNum) || yearNum < 2010 || yearNum > currentYear) {
      setYearError(`Έτος πρέπει να είναι μεταξύ 2010 και ${currentYear}`);
      hasErrors = true;
    }

    if (!court || court === "") {
      setCourtError("Παρακαλώ επιλέξτε δικαστήριο");
      hasErrors = true;
    }

    if (hasErrors) {
      return;
    }

    // Check case limit client-side
    if (allCases.length >= 500) {
      setErrMsg("Έχετε φτάσει το μέγιστο όριο υποθέσεων (500)");
      return;
    }

    setIsSubmitting(true);
    setErrMsg("");

    try {
      const response = await axiosPrivate.post(
        "/cases",
        JSON.stringify({ gak, year, court, note: note.substring(0, 50) }),
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        }
      );
      handleClickAddCaseModal();
      setCredits(credits - 30);

      // If server returned updated case data, use it; otherwise use the input data
      const caseData = response.data.case || { gak, year, court, note: note.substring(0, 50) };
      handleAddCase(caseData);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 409) {
        // Duplicate case
        setErrMsg(err.response.data.message || "Έχετε ξανακαταχωρήσει την υπόθεση");
      } else if (err.response?.status === 401 || err.response?.status === 403) {
        // Auth error - redirect to login
        navigate("/login", { state: { from: location }, replace: true });
      } else {
        // Generic error
        setErrMsg("Σφάλμα κατά την προσθήκη. Παρακαλώ δοκιμάστε ξανά.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="container_ac" onKeyDown={handleClickAddCaseModal}>
      <p
        ref={errRef}
        className={errMsg ? "errmsg" : "offscreen"}
        aria-live="assertive"
      >
        {errMsg}
      </p>

      <div className="close_addcase">
        <Icon
          onClick={() => handleClickAddCaseModal()}
          className="cursor-pointer"
          icon={XCircleIcon}
          size="lg"
          color="red"
          tooltip="Κλείσιμο"
        />
      </div>
      {/* <span></span> */}
      {/* <h1>Sign In</h1> */}
      <form className="form" onSubmit={handleSubmit}>
        {/* <label htmlFor="username">Email:</label> */}
        <div className="input-container ic1">
          <TextInput
            placeholder="ΓΑΚ"
            type="number"
            id="gak"
            onValueChange={handleGakChange}
            value={gak}
            error={gakError !== ""}
            errorMessage={gakError}
            required
            style={{ fontSize: "18px", height: "48px" }}
          />
        </div>

        {/* <label htmlFor="password">Password:</label> */}
        <div className="input-container ic2">
          <TextInput
            placeholder="Έτος"
            type="number"
            id="year"
            onValueChange={handleYearChange}
            value={year}
            error={yearError !== ""}
            errorMessage={yearError}
            required
            style={{ fontSize: "18px", height: "48px" }}
          />
        </div>
        <div className="input-container ic2">
          <SearchSelect
            id="court"
            name="court"
            value={court}
            onValueChange={handleCourtChange}
            placeholder="Δικαστήριο"
            error={courtError !== ""}
            errorMessage={courtError}
          >
            {options.map((option) => (
              <SearchSelectItem
                key={option.value.trim()}
                value={option.value.trim()}
              >
                {option.label.trim()}
              </SearchSelectItem>
            ))}
          </SearchSelect>
        </div>
        <div className="input-container ic2">
          <TextInput
            placeholder="Σημείωση"
            type="text"
            id="note"
            onValueChange={(value) => setNote(value)}
            value={note}
            style={{ fontSize: "18px", height: "48px" }}
          />
        </div>
        <button type="submit" className="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <span className="flex items-center justify-center">
              <svg
                className="animate-spin h-5 w-5 mr-2"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Προσθήκη...
            </span>
          ) : (
            "Προσθήκη"
          )}
        </button>
      </form>
    </section>
  );
};

export default AddCase;
